import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import { InSubject } from '@atmi/core/utils';

import {BehaviorSubject, combineLatest, firstValueFrom} from 'rxjs';
import { filter, first, map, shareReplay, switchMap, takeUntil } from 'rxjs/operators';

import { BaseCtrlValueAccessorFormComponent } from '@sim/form';
import { ClassName } from '@sim/role';

import { BaseApiService, CardsApiService } from '@atmi/core';
import {Base, cardS} from '@atmi/core/models';
import {CardS} from '@atmi/models/cards/card-s';
import { BasePickerDialogComponent } from '@atmi/shared/base-picker-dialog/base-picker-dialog.component';
import { makeQueryBuilder, makeQueryRules } from '@ui/ui.module';

@Component({
  selector: 'app-info-card-s-manager',
  styleUrls: ['./info-card-s-manager.component.scss'],
  templateUrl: './info-card-s-manager.component.html',
})
export class InfoCardSManagerComponent extends BaseCtrlValueAccessorFormComponent<any> implements OnInit {

  @Input() classNames: ClassName[] = [];

  @Input() @InSubject() cardId?: string;
  private readonly cardIdChange = new BehaviorSubject<string | undefined>(undefined);

  @Input() set isOperaMadre(v: null | boolean) {
    this.isOperaMadreEnableControl.setValue(v);
  }
  readonly isOperaMadreEnableControl = new UntypedFormControl(false);

  readonly childNumberCount$ = this.cardIdChange.pipe(
    filter((cardId): cardId is string => !!cardId),
    switchMap((cardId) => {
      const rules = makeQueryRules({ benePadre: cardId, tipoOperaFiglia: 'Numero' }, cardS.CardSFiltersMeta);
      const query = makeQueryBuilder(rules);

      return this.cardsApi.indexS({ page: 1, limit: 1, filter: query });
    }),
    map((page) => page.count),
    shareReplay(1),
  );

  readonly childProvaCount$ = this.cardIdChange.pipe(
    filter((cardId): cardId is string => !!cardId),
    switchMap((cardId) => {
      const rules = makeQueryRules({ benePadre: cardId, tipoOperaFiglia: 'Prova d\'Artista - EA' }, cardS.CardSFiltersMeta);
      const query = makeQueryBuilder(rules);

      return this.cardsApi.indexS({ page: 1, limit: 1, filter: query });
    }),
    map((page) => page.count),
    shareReplay(1),
  );

  readonly childFuoriCommercioCount$ = this.cardIdChange.pipe(
    filter((cardId): cardId is string => !!cardId),
    switchMap((cardId) => {
      const rules = makeQueryRules({ benePadre: cardId, tipoOperaFiglia: 'Fuori Commercio - HC' }, cardS.CardSFiltersMeta);
      const query = makeQueryBuilder(rules);

      return this.cardsApi.indexS({ page: 1, limit: 1, filter: query });
    }),
    map((page) => page.count),
    shareReplay(1),
  );

  readonly productExamples$ = combineLatest([
    this.childNumberCount$,
    this.childFuoriCommercioCount$,
    this.childProvaCount$,
  ]).pipe(
    map(([numberCount, fuoriCommercio, prova]) => numberCount + fuoriCommercio + prova),
  );

  base?: Base;
  parentCard?: CardS;

  constructor(
    private readonly baseApi: BaseApiService,
    private readonly cardsApi: CardsApiService,
    private readonly dialog: MatDialog,
    private readonly cd: ChangeDetectorRef,
  ) {
    super();

    this.formChange
      .pipe(
        filter((form): form is UntypedFormGroup => !!form),
        takeUntil(this.unsubscription$),
      )
      .subscribe((form) => {
        this.getBase(form.value.base);
        this.getParentCard(form.value.benePadre);
      });
  }

  ngOnInit() {
    super.ngOnInit();
  }

  async showAddBaseDialogHandler() {
    const ref = this.dialog.open(BasePickerDialogComponent, { data: { base: this.base }, width: '1000px' });
    const res: Base | undefined = await firstValueFrom(ref.afterClosed().pipe(first()));
    if (res && this.form) {
      this.form.controls.base.setValue(res.id);

      this.getBase(res.id);
    }
  }

  unlinkParentCardHandler() {
    if (!this.form) {
      throw new Error('Cannot use form without a value');
    }

    this.form.controls.benePadre.setValue(null);
    this.parentCard = undefined;
  }

  removeBaseHandler() {
    if (!this.form) {
      throw new Error('Cannot use form without a value');
    }

    this.form.controls.base.setValue(null);
    this.base = undefined;
  }

  getProductExamplesToProduct(productExamples: number | null) {
    if (this.form) {
      const operaMadre = this.form.controls.operaMadre as UntypedFormGroup;
      const tiratura = operaMadre.controls.tiratura.value;
      const tiraturaEA = operaMadre.controls.tiraturaEA.value;
      const tiraturaHC = operaMadre.controls.tiraturaHC.value;
      return (tiratura + tiraturaEA + tiraturaHC) - (productExamples || 0);
    }
    return productExamples;
  }

  private async getBase(baseId?: string) {
    if (!baseId) {
      this.base = undefined;
      return;
    }

    try {
      this.base = await firstValueFrom(this.baseApi.get(baseId).pipe(first()));
      this.cd.markForCheck();
    } catch (e) {
      throw new Error(e);
    }
  }

  private async getParentCard(cardId?: string) {
    if (!cardId) {
      this.parentCard = undefined;
      return;
    }

    try {
      this.parentCard = await firstValueFrom(this.cardsApi.getS(cardId).pipe(first()));
      this.cd.markForCheck();
    } catch (e) {
      throw new Error(e);
    }
  }
}
